import { Button, Modal, Result, Spin } from 'antd'
import { getErrorMessage } from '@/utils/error.ts'
import { CustomerSendPreview } from './customer-send-preview.tsx'
import { CustomerSendResult } from './customer-send-result.tsx'
import { useCustomerSendPreview } from './hooks/use-customer-send-preview.ts'
import { useCustomerSendSubmission } from './hooks/use-customer-send-submission.ts'

export function CustomerSendModal({
  customerId,
  customerName,
  open,
  onClose,
  afterClose
}: {
  customerId: number
  customerName: string
  open: boolean
  onClose: () => void
  afterClose: () => void
}) {
  const { send, submit, retry, canRetry } = useCustomerSendSubmission()
  const { query: preview, ...selection } = useCustomerSendPreview({
    customerId,
    enabled: open && send.isIdle
  })

  const confirm = () => {
    if (!selection.canSend || !preview.data || !selection.botId) return
    void submit({
      customerId,
      ...selection.range,
      botId: selection.botId,
      expectedState: preview.data.expectedState,
      idempotencyKey: crypto.randomUUID(),
      accounts: preview.data.organizations.map((org) => ({
        organizationId: org.id,
        accountId: selection.accounts[org.id]!
      }))
    })
  }
  let content
  if (send.isPending) {
    content = (
      <Result
        icon={<Spin size="large" />}
        title="正在合并账单并生成 PDF"
        subTitle="正在生成正式账单组和发送任务，请保持页面打开。"
      />
    )
  } else if (send.isError) {
    content = (
      <Result
        status="error"
        title="提交未完成"
        subTitle={getErrorMessage(send.error, '请重试或重新预览')}
        extra={
          <Button type="primary" onClick={retry}>
            {canRetry ? '重试发送' : '重新预览'}
          </Button>
        }
      />
    )
  } else if (send.isSuccess) {
    content = (
      <CustomerSendResult
        customerId={customerId}
        deliveryId={send.data.deliveryId}
        groupIds={send.data.groupIds}
        initialStatus={send.data.status}
        open={open}
      />
    )
  } else {
    content = (
      <CustomerSendPreview
        customerName={customerName}
        data={preview.data}
        range={selection.range}
        pagination={selection.pagination}
        accounts={selection.accounts}
        botId={selection.botId}
        loading={preview.isFetching}
        error={preview.isError ? getErrorMessage(preview.error, '读取账单失败') : undefined}
        onRangeChange={selection.changeRange}
        onPageChange={selection.changePage}
        onBotChange={selection.changeBot}
        onAccountChange={selection.changeAccount}
        onRefresh={() => void preview.refetch()}
      />
    )
  }
  return (
    <Modal
      open={open}
      title={`${customerName} · 发送收款账单`}
      width="72rem"
      centered
      closable={!send.isPending}
      keyboard={!send.isPending}
      mask={{ closable: false }}
      onCancel={onClose}
      afterClose={afterClose}
      footer={
        send.isPending ? null : (
          <>
            <Button onClick={onClose}>{send.isSuccess || send.isError ? '关闭' : '取消'}</Button>
            {!send.isSuccess && !send.isError && (
              <Button type="primary" disabled={!selection.canSend} onClick={confirm}>
                合并账单并发送 PDF
              </Button>
            )}
          </>
        )
      }
    >
      {content}
    </Modal>
  )
}
